import React from 'react';
import { ClassValue } from 'classnames/types';
import styles from './Headline.module.scss';
import Typography, { TypographyProps, SupportedTagName } from '../Typography';
import { ArrayElementType } from '../../../types/utils/ArrayElementType';

export type HeadlineProps<T extends SupportedTagName> = {
  /** Modifier for the font size. Font sizes are responsive */
  size?: SupportedHeadlineSize;
  /** Allow using small caps in the Headline text. Meant to be used in the special cases such "iPhone" */
  allowSmallCaps?: boolean;
  /** Allowed colors for headline */
  color?: SupportedHeadlineColor;
} & Omit<TypographyProps<T>, 'fontWeight' | 'font' | 'uppercase' | 'color'>;
// Alias for backwards compatibility
export type HeadlineAttributes = HeadlineProps<SupportedTagName>;

const headlineColor = {
  white: 'white',
  black: 'black',
  purple500: 'purple500',
} as const;

function Headline<T extends SupportedTagName>({
  tagName,
  size = 'xlg',
  children,
  className,
  allowSmallCaps = true,
  color = 'black',
  ...props
}: HeadlineProps<T>) {
  const classNames: ClassValue = [
    styles[`headline__size_${size.toLowerCase()}`],
    className,
  ];

  return (
    <Typography
      {...props}
      tagName={tagName as SupportedTagName} // this circumvents some weird ts problem
      className={classNames}
      fontWeight="bold"
      font="teliasans"
      uppercase={!allowSmallCaps}
      color={headlineColor[color]}
    >
      {children}
    </Typography>
  );
}

export const supportedHeadlineSizes = ['xlg', 'lg', 'md', 'sm', 'xs'] as const;

type SupportedHeadlineSize = ArrayElementType<typeof supportedHeadlineSizes>;

export const supportedHeadlineColors = ['black', 'white', 'purple500'] as const;

export type SupportedHeadlineColor = ArrayElementType<
  typeof supportedHeadlineColors
>;

export default Headline;
